import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * A resolved status style: the display label plus its sanctioned colour
 * className. Per-domain style-map modules (e.g.
 * `features/shipments/utils/status-style.ts`) resolve a raw status value into
 * one of these so the same status renders identically everywhere it appears.
 *
 * Colour vocabulary (DESIGN.md §1/§7) — one colour, one meaning:
 *  - amber   → pending / warning / attention
 *  - cyan    → in-motion / in-progress
 *  - emerald → done / completed / positive
 *  - muted   → terminal / neutral / infra
 *  - red     → failed / error
 */
export interface StatusStyle {
  label: string;
  className: string;
}

/** Shared pill geometry — a soft fill badge with no border (matches the PO +
 *  shipments status pills). Colour comes from the resolved {@link StatusStyle}. */
export const statusBadgeBase =
  "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap";

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** Resolved status: display label + sanctioned colour className. */
  status: StatusStyle;
}

/**
 * Presentational status pill. Pass a resolved {@link StatusStyle} from a
 * per-domain style map. Additional `className` (sizing overrides, etc.) merges
 * last via tailwind-merge, so callers can widen/resize without forking styles.
 */
export function StatusBadge({ status, className, ...props }: StatusBadgeProps) {
  return (
    <span
      className={cn(statusBadgeBase, status.className, className)}
      {...props}
    >
      {status.label}
    </span>
  );
}
